"use client";
import React, { useState } from "react";
import { Expense } from "@/enums/errorMessages";
import {
  deleteExpenseApi,
  getExpensesApi,
} from "@/modules/organizationServices";
import {
  deleteExpense,
  setExpenses,
} from "@/store/redux/organization/expenseSlice";
import { RootState } from "@/store/store";

import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { useNavigate, useSearchParams } from "react-router-dom";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/uiComponents/components/ui/table";
import { Button } from "@/uiComponents/components/ui/button";
import { ROUTES } from "@/enums/routes";

import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/uiComponents/components/ui/dropdown-menu";
import {
  ArrowDownToLine,
  ChevronLeft,
  ChevronRight,
  EllipsisVertical,
  SquarePen,
  Trash2,
} from "lucide-react";

import {
  PaymentMethodsLabels,
  PaymentMethodsValues,
} from "@/enums/expenseFields";

import ImageModal from "../ImageModal";
import { GetExpense, GetExpenseResponse } from "@/types/expense";
import {
  getExpenseEditAccess,
  getExpenseTextClass,
} from "@/utils/expenseDateHelper";
import { ErrorHelper } from "@/utils/errorHelper";
import DeleteModal from "../DeleteModal";
import { ExpenseFilter } from "./ExpenseFilter";
import ExpenseDetail from "./ExpenseDetail";
import { toastMessage } from "@/utils/toastHelper";
import { TiTick } from "react-icons/ti";
import { formattedItems } from "@/utils/formattedItems";

const Expenses = () => {
  const expenses = useSelector(
    (state: RootState) => state.expense.getExpensesResponse.results
  );

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const role = useSelector((state: RootState) => state.userDetail.role);
  const pages = useSelector(
    (state: RootState) => state.expense.getExpensesResponse.count
  );
  const totalPages = Math.ceil(pages / 10);
  const filter = useSelector((state: RootState) => state.filter.filter);

  const loading = useSelector((state: RootState) => state.app.loading);
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = Number(searchParams.get("page")) || 1;
  const [selectedExpense, setSelectedExpense] = useState<GetExpense | null>(
    null
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [nextPageUrl, setNextPageUrl] = React.useState<string | null>(null);
  const [prevPageUrl, setPrevPageUrl] = React.useState<string | null>(null);
  const [expense, setExpense] = useState<GetExpense | null>(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  const [sortExpenseByAmount, setSortExpenseByAmount] = useState<{
    key: "amount";
    direction: "asc" | "desc";
  } | null>(null);

  const getExpenses = async (pageNum: number) => {
    try {
      const response = await getExpensesApi(pageNum, filter);

      const fixedResponse: GetExpenseResponse = {
        count: response.count ?? 0,
        results: response.results ?? [],
        next: response.next ?? "",
        previous: response.previous ?? "",
      };
      if (response.count !== undefined) {
        dispatch(setExpenses(fixedResponse));
      }

      setNextPageUrl(response.next ?? null);
      setPrevPageUrl(response.previous ?? null);

      setSearchParams({ page: pageNum.toString() });
    } catch (error) {
      const actualError = ErrorHelper(error);
      const message = Array.isArray(actualError) ? actualError[0] : actualError;
      toastMessage("error", message);
    }
  };

  useEffect(() => {
    const page = Number(searchParams.get("page")) || 1;
    getExpenses(page);
  }, []);

  const handleDeleteExpense = async (id: number) => {
    try {
      await deleteExpenseApi(id, role);
      dispatch(deleteExpense(id));
      toastMessage("success", Expense.EXPENSE_DELETE);
      await getExpenses(currentPage);
    } catch (error) {
      const actualError = ErrorHelper(error);
      const message = Array.isArray(actualError) ? actualError[0] : actualError;
      toastMessage("error", message);
    }
  };

  const requestSort = (key: "amount") => {
    let direction: "asc" | "desc" = "asc";
    if (
      sortExpenseByAmount &&
      sortExpenseByAmount.key === key &&
      sortExpenseByAmount.direction === "asc"
    ) {
      direction = "desc";
    }
    setSortExpenseByAmount({ key, direction });
  };
  const sortedExpenses = [...expenses].sort((a, b) => {
    if (!sortExpenseByAmount) return 0;
    const valueA = Number(a[sortExpenseByAmount.key]);
    const valueB = Number(b[sortExpenseByAmount.key]);

    if (valueA < valueB) {
      return sortExpenseByAmount.direction === "asc" ? -1 : 1;
    }
    if (valueA > valueB) {
      return sortExpenseByAmount.direction === "asc" ? 1 : -1;
    }
    return 0;
  });

  if (loading) {
    return (
      <div className="flex h-40 w-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col max-w-screen">
      <h2 className="text-2xl  font-semibold w-full">Expense History</h2>

      <div className="overflow-x-auto  mt-5 rounded-sm">
        <Table className="min-w-full bg-white border-2">
          <TableHeader>
            <TableRow>
              <TableCell colSpan={15} className="p-1 align-middle">
                <div className="flex gap-4">
                  <div className="w-full [&_button]:hidden [&>*]:my-0 [&>*]:p-0  p-2 pt-4 pb-4 overflow-hidden">
                    <ExpenseFilter height={10} />
                  </div>

                  <Button
                    variant={"outline"}
                    className=" bg-white
             hover:bg-gray-300 active:bg-[#0D1117] cursor-pointer active:text-white mt-4 h-10 mr-1"
                    onClick={() => navigate(ROUTES.EXPORT_PDF)}
                  >
                    <ArrowDownToLine />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          </TableHeader>

          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Item</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>
                <div
                  className="flex cursor-pointer"
                  onClick={() => requestSort("amount")}
                >
                  Amount (NPR.)
                  {sortExpenseByAmount?.key === "amount" && (
                    <span className="ml-1">
                      {sortExpenseByAmount.direction === "asc" ? "↑" : "↓"}
                    </span>
                  )}
                </div>
              </TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Branch</TableHead>
              <TableHead>Payment Method</TableHead>
              <TableHead>Bill Photo</TableHead>
              <TableHead>Reimbursed</TableHead>
              <TableHead>Reimbursed To</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(sortExpenseByAmount ? sortedExpenses : expenses).length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={10}
                  className="text-center text-gray-500 py-4"
                >
                  No expenses to show
                </TableCell>
              </TableRow>
            ) : (
              (sortExpenseByAmount ? sortedExpenses : expenses).map(
                (expense) => (
                  <>
                    <TableRow
                      key={expense.id}
                      className={getExpenseTextClass(role, expense.createdAt)}
                      onClick={() => {
                        setIsDetailOpen(true);
                        setExpense(expense);
                      }}
                    >
                      <TableCell className="text-left">
                        {expense.date}
                      </TableCell>
                      <TableCell className="text-left">
                        {formattedItems(expense.items)}
                      </TableCell>
                      <TableCell className="text-left">
                        {expense.categoryName}
                      </TableCell>
                      <TableCell className="text-center">
                        {expense.amount}
                      </TableCell>
                      <TableCell className="text-left">
                        {expense.description.length > 20
                          ? `${expense.description.slice(0, 20)}...`
                          : expense.description}
                      </TableCell>
                      <TableCell className="text-center">
                        {expense.branchName}
                      </TableCell>
                      <TableCell className="text-center">
                        {expense.paymentMethod === PaymentMethodsValues.QR
                          ? PaymentMethodsLabels.ONLINE
                          : expense.paymentMethod ===
                              PaymentMethodsValues.CHEQUE
                            ? PaymentMethodsLabels.CHEQUE
                            : expense.paymentMethod[0].toUpperCase() +
                              expense.paymentMethod.slice(1)}
                      </TableCell>
                      <TableCell>
                        {Array.isArray(expense.billPhotos) &&
                        expense.billPhotos.length > 0
                          ? (() => {
                              //@ts-expect-error:skip next line
                              const firstPhoto = expense.billPhotos[0].photo;
                              const isString = typeof firstPhoto === "string";
                              const isPdf =
                                isString &&
                                firstPhoto.toLowerCase().endsWith(".pdf");
                              return isPdf ? (
                                <div className="relative w-full h-full">
                                  <object
                                    data={firstPhoto}
                                    type="application/pdf"
                                    className="w-full h-full"
                                  />
                                </div>
                              ) : (
                                <div className="flex gap-4">
                                  <img
                                    key={firstPhoto}
                                    src={firstPhoto}
                                    alt={`Bill Photo`}
                                    className="h-10 w-10 object-cover cursor-pointer"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setIsModalOpen(true);
                                      setSelectedExpense(expense);
                                    }}
                                  />
                                </div>
                              );
                            })()
                          : "No Photo"}
                      </TableCell>
                      <TableCell className="text-center">
                        {expense.reimbursed ? (
                          <div className="flex justify-center">
                            <TiTick className="text-green-500" size={20} />
                          </div>
                        ) : (
                          ""
                        )}
                      </TableCell>

                      <TableCell className="text-center">
                        {expense.reimbursedTo}
                      </TableCell>

                      <TableCell colSpan={10} className="text-right pr-4">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              className="h-10 w-10 p-0 cursor-pointer hover:bg-gray-200 "
                            >
                              <EllipsisVertical />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent>
                            <DropdownMenuItem
                              disabled={getExpenseEditAccess(
                                role,
                                expense.createdAt
                              )}
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(
                                  `${ROUTES.UPDATE_EXPENSE}/${expense.id}`,
                                  {
                                    state: { expense },
                                  }
                                );
                              }}
                              className="cursor-pointer text-left hover:bg-gray-300 text-sm text-gray-800 px-3 py-2"
                            >
                              <div className="flex gap-2">
                                <SquarePen size={16} /> Edit
                              </div>
                            </DropdownMenuItem>

                            {role.isSuperAdmin && (
                              <DropdownMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedExpense(expense);
                                  setIsDeleteOpen(true);
                                }}
                                className="cursor-pointer text-left hover:bg-gray-300 text-sm text-gray-800 px-3 py-2"
                              >
                                <div className="flex gap-2">
                                  <Trash2 size={16} /> Delete
                                </div>
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  </>
                )
              )
            )}
          </TableBody>
        </Table>
        {selectedExpense !== null && (
          <ImageModal
            onClose={() => setIsModalOpen(false)}
            isOpen={isModalOpen}
            expense={selectedExpense}
          />
        )}
        {expense && (
          <ExpenseDetail
            isOpen={isDetailOpen}
            onClose={() => setIsDetailOpen(false)}
            expense={expense}
          />
        )}

        {selectedExpense && (
          <DeleteModal
            isOpen={isDeleteOpen}
            onClose={() => setIsDeleteOpen(false)}
            item={selectedExpense}
            onConfirm={(item: GetExpense) => {
              handleDeleteExpense(item.id);
              setIsDeleteOpen(false);
              setSelectedExpense(null);
            }}
            itemLabel="expense"
          />
        )}
      </div>
      <div className="flex gap-2 m-4 justify-center">
        {currentPage !== totalPages + 1 - totalPages && (
          <Button
            variant={"outline"}
            className=" hover:bg-gray-300 cursor-pointer"
            onClick={() => getExpenses(currentPage - 1)}
            disabled={!prevPageUrl || currentPage === 1}
          >
            <ChevronLeft />
          </Button>
        )}

        {currentPage > 1 && (
          <Button
            variant={"outline"}
            className=" hover:bg-gray-300 cursor-pointer"
            onClick={() => getExpenses(currentPage - 1)}
            disabled={currentPage === 1}
          >
            {currentPage - 1}
          </Button>
        )}

        <Button
          className="bg-[#191F29] cursor-not-allowed"
          onClick={() => getExpenses(currentPage)}
        >
          {currentPage}
        </Button>

        {currentPage < totalPages && (
          <Button
            variant={"outline"}
            className=" hover:bg-gray-300 cursor-pointer"
            onClick={() => getExpenses(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            {currentPage + 1}
          </Button>
        )}

        {currentPage < totalPages && (
          <Button
            variant={"outline"}
            className=" hover:bg-gray-300 cursor-pointer"
            onClick={() => getExpenses(currentPage + 1)}
            disabled={!nextPageUrl || currentPage === totalPages}
          >
            <ChevronRight />
          </Button>
        )}
      </div>
    </div>
  );
};

export default Expenses;
